import * as R from 'ramda';
import React from 'react';
import { arrayOf, func, shape, number, oneOfType, string } from 'prop-types';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';
import DateTime from 'common/components/dateTime';
import ScrollContainer from 'common/mdc/scrollbar/container';

import Table, {
  TableRow,
  TableCell,
  TableHead,
} from 'common/mdc/table';

import {
  COLUMN_SAMPLE_ID,
  COLUMN_NODE_TIME,
  COLUMN_SERVER_TIME,
} from '@sma/service/api/meteringProfileSamples';

import RegisterName from '@sma/components/device/onDemandReading/name';
import registerShape from '@sma/components/device/onDemandReading/shape';
import meteringProfileShape from '@sma/components/device/meteringProfile/shape';
import { compareRegisters } from '@sma/store/device/meteringProfile/selectors';
import RegisterSampleValue from './sampleValue';

export const messages = defineMessages({
  meterTime: {
    id: 'sample.header.meterTime',
    defaultMessage: 'Meter time',
  },
  serverTime: {
    id: 'sample.header.serverTime',
    defaultMessage: 'Server time',
  },
  sampleId: {
    id: 'sample.header.sampleId',
    defaultMessage: 'Sample ID',
  },
  nodeTime: {
    id: 'sample.header.nodeTime',
    defaultMessage: 'Node time',
  },
});

export const sampleHead = ({
  toggleSort,
  isSorted,
  registers,
  intl,
  ...props
}) => (
  <TableHead {...props}>
     <TableCell
      sortable
      columnId={COLUMN_SAMPLE_ID}
      onClick={() => toggleSort(COLUMN_SAMPLE_ID)}
      sorted={isSorted(COLUMN_SAMPLE_ID)}
      title={intl.formatMessage(messages.sampleId)}
      style={{ minWidth: '190px' }}
     ><FormattedMessage {...messages.sampleId} />
    </TableCell>
    <TableCell
      sortable
      columnId={COLUMN_SERVER_TIME}
      onClick={() => toggleSort(COLUMN_SERVER_TIME)}
      sorted={isSorted(COLUMN_SERVER_TIME)}
      title={intl.formatMessage(messages.serverTime)}
      style={{ minWidth: '190px' }}
      alignRight
    ><FormattedMessage {...messages.serverTime} />
    </TableCell>
    <TableCell
      sortable
      columnId={COLUMN_NODE_TIME}
      onClick={() => toggleSort(COLUMN_NODE_TIME)}
      sorted={isSorted(COLUMN_NODE_TIME)}
      title={intl.formatMessage(messages.nodeTime)}
      style={{ minWidth: '190px' }}
      alignRight
    ><FormattedMessage {...messages.nodeTime} />
    </TableCell>
    {registers.map(
      (column, index) => (
        <TableCell
          key={index}
          columnId={index}
          flex="2"
          alignRight
        >
          <RegisterName register={column} title />
        </TableCell>
      ),
    )}
  </TableHead>
);

sampleHead.propTypes = {
  registers: arrayOf(registerShape).isRequired,
  isSorted: func.isRequired,
  toggleSort: func.isRequired,
  intl: intlShape.isRequired,
};

export const sampleRow = ({ key, meteringProfile, registers, ...props }) => (sample) => {
  const samples = (sample && sample.values) || [];
  const getRegisterSample = register => R.find(compareRegisters(register), samples);

  return (
    <TableRow
      key={key}
      {...props}
    >
      <TableCell columnId={COLUMN_SAMPLE_ID}>
      {sample.sampleId}
      </TableCell>
      <TableCell columnId={COLUMN_SERVER_TIME}>
        {sample.serverTime && (
          <DateTime value={sample.serverTime} seconds />
        )}
      </TableCell>
      <TableCell columnId={COLUMN_NODE_TIME}>
        {sample.nodeTime && (
          <DateTime value={sample.nodeTime} seconds />
        )}
      </TableCell>
      {registers.map((register, index) => {
        const registerSample = getRegisterSample(register);

        return (
          <TableCell key={index} columnId={index}>
            {registerSample && (
              registerSample.rawValue ?
                <RegisterSampleValue
                  sampleValue={registerSample.rawValue}
                  stringValue={registerSample.value}
                /> :
                registerSample.value
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const MeteringProfileSamplesTable = ({
  samples,
  meteringProfile,
  isSorted,
  toggleSort,
  registers,
  intl,
  ...props
}) => (
  <ScrollContainer useRef="tableRef" settings={{ suppressScrollY: true }}>
    <Table className="samplesTable" {...props}>
      {sampleHead({
        isSorted,
        toggleSort,
        registers,
        intl,
      })}
      {samples.map((sample, key) => sampleRow({ key, meteringProfile, registers })(sample))}
    </Table>
  </ScrollContainer>
);

MeteringProfileSamplesTable.propTypes = {
  samples: arrayOf(shape({
    id: number,
    value: oneOfType([string, number]),
  })).isRequired,
  registers: arrayOf(registerShape).isRequired,
  meteringProfile: meteringProfileShape.isRequired,
  isSorted: func.isRequired,
  toggleSort: func.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(MeteringProfileSamplesTable);
